import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import * as XLSX from 'xlsx'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../auth'
import {useGlobalFilter} from '../../../../shared/context/GlobalFilterContext'
import {getVesselList} from '../../../Management/core/_requests'
import {toast, ToastContainer} from 'react-toastify'
import type {Vessel} from '../../../Management/core/_models'
import {getAccessibleVessels, isVesselAssignedUserOnly} from '../../core/technicalScopeUtils'
import {TECH_PERM} from '../../core/technicalPermissions'
import {TechnicalAccessDenied} from '../TechnicalAccessDenied'
import type {
  PmsCriticality,
  PmsJobCombinedDto,
  PmsJobRecord,
  PmsJobState,
  PmsPlanLineDto,
  PmsJobDueReason,
} from '../../core/pms/_models'
import {
  forceOverduePlanLineJob,
  getPmsPlanLine,
  listWorkOrdersByPlanLine,
  searchPmsJobsCombined,
} from '../../core/pms/_requests'
import {PmsJobDetailModal} from './PmsJobDetailModal'
import {CompleteJobModal} from './CompleteJobModal'
import {EditPlanLineModal} from './EditPlanLineModal'
import {getDisplayedJobState, isMaskedJob} from './pmsJobHelpers'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type CalendarView = 'MONTH' | 'WEEK' | 'YEAR' | 'CUSTOM'

type CalendarItemKind = 'PLANNED' | 'OVERDUE' | 'CLOSED' | 'UNPLANNED_CREATED' | 'JOB'

type CalendarItem = {
  id: string
  date: Date
  title: string
  subtitle?: string
  state?: PmsJobState | 'PERIODICITY'
  criticality?: PmsCriticality | null
  source: 'job' | 'periodicity'
  vesselId?: number
  vesselName?: string | null
  job?: PmsJobRecord
  planLine?: PmsPlanLineDto
  isOverdue?: boolean
  isClosed?: boolean
  kind: CalendarItemKind
}

type JobWithPlanLine = PmsJobRecord & {
  __planLine?: PmsPlanLineDto
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const isRunningHoursSchedule = (scheduleType?: string | null) =>
  scheduleType === 'RUNNING_HOURS' ||
  scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
  scheduleType === 'HYBRID_BOTH_REQUIRED'

const isTimeSchedule = (scheduleType?: string | null) =>
  scheduleType === 'TIME' ||
  scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
  scheduleType === 'HYBRID_BOTH_REQUIRED'

const isPeriodicityOverdue = (line?: PmsPlanLineDto | null): boolean => {
  if (!line || line.active === false) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const hasTime = isTimeSchedule(line.scheduleType ?? undefined)
  const hasCounter = isRunningHoursSchedule(line.scheduleType ?? undefined)

  let timeDue = false
  let counterDue = false

  if (hasTime && line.nextDueDate) {
    const dueDate = new Date(line.nextDueDate)
    dueDate.setHours(0, 0, 0, 0)
    timeDue = dueDate <= today
  }

  if (hasCounter && line.nextDueCounter != null && line.runningHourCounterValue != null) {
    counterDue = line.runningHourCounterValue >= line.nextDueCounter
  }

  if (line.scheduleType === 'HYBRID_BOTH_REQUIRED') return timeDue && counterDue
  if (line.scheduleType === 'HYBRID_WHICHEVER_FIRST') return timeDue || counterDue
  if (line.scheduleType === 'TIME') return timeDue
  if (line.scheduleType === 'RUNNING_HOURS') return counterDue
  return false
}

const toDateKey = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const startOfWeek = (date: Date) => {
  const copy = new Date(date)
  const day = copy.getDay()
  const diff = copy.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(copy.setDate(diff))
  monday.setHours(0, 0, 0, 0)
  return monday
}

const endOfWeek = (date: Date) => {
  const start = startOfWeek(date)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  return end
}

const startOfMonth = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1)
  start.setHours(0, 0, 0, 0)
  return start
}

const endOfMonth = (date: Date) => {
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  end.setHours(23, 59, 59, 999)
  return end
}

const formatShortDate = (date: Date) =>
  date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})

const formatMonthLabel = (year: number, month: number) =>
  new Date(year, month, 1).toLocaleDateString('en-US', {month: 'long', year: 'numeric'})

const MODAL_OVERLAY_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const DEFAULT_FORCE_OVERDUE_REASON: PmsJobDueReason = 'OTHER'
const FORCE_OVERDUE_REASON_OPTIONS: {value: PmsJobDueReason; label: string}[] = [
  {value: 'DEFECT', label: 'Defect'},
  {value: 'BREAKDOWN', label: 'Breakdown'},
  {value: 'SAFETY_CONCERN', label: 'Safety Concern'},
  {value: 'OTHER', label: 'Other'},
]

const scheduleTypeLabel = (value?: string | null) => {
  switch (value) {
    case 'TIME':
      return 'Time'
    case 'RUNNING_HOURS':
      return 'Running Hours'
    case 'HYBRID_WHICHEVER_FIRST':
      return 'Hybrid (Whichever First)'
    case 'HYBRID_BOTH_REQUIRED':
      return 'Hybrid (Both Required)'
    case 'EVENT':
      return 'Event'
    case 'DOCK':
      return 'Dock'
    case 'AS_REQUIRED':
      return 'As Required'
    default:
      return '-'
  }
}

const getPlanLineIdFromItem = (item: CalendarItem): number | null => {
  const job = item.job as PmsJobRecord | undefined
  return item.planLine?.id ?? job?.planLineId ?? null
}

const CalendarPlanningBoard: FC = () => {
  const {currentUser, isSuperAdmin, hasPermission} = useAuth()
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const canReadCalendar = hasPermission(TECH_PERM.READ_PMS_CALENDAR)
  const canCreateCalendar = hasPermission(TECH_PERM.CREATE_PMS_CALENDAR)
  const canUpdateCalendar = hasPermission(TECH_PERM.UPDATE_PMS_CALENDAR)
  const canDeleteCalendar = hasPermission(TECH_PERM.DELETE_PMS_CALENDAR)
  const canExportPms = hasPermission(TECH_PERM.EXPORT_PMS)

  const [selectedVesselId, setSelectedVesselId] = useState<string>('')

  // Sync global header vessel & company filters → local selectedVesselId
  useEffect(() => {
    const gvId = globalState.vessel?.id
    if (gvId != null) {
      setSelectedVesselId(String(gvId))
    } else if (isVesselOnlyUser && currentUser?.vessel?.id) {
      setSelectedVesselId(String(currentUser.vessel.id))
    } else {
      setSelectedVesselId('')
    }
  }, [globalState.vessel, globalState.company, isVesselOnlyUser, currentUser])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [combinedItems, setCombinedItems] = useState<PmsJobCombinedDto[]>([])
  const [stateFilter, setStateFilter] = useState<PmsJobState | 'ALL'>('ALL')
  const [criticalityFilter, setCriticalityFilter] = useState<PmsCriticality | 'ALL'>('ALL')
  const [loading, setLoading] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [viewing, setViewing] = useState<PmsJobRecord | null>(null)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [completing, setCompleting] = useState<JobWithPlanLine | null>(null)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [loadingEditLineId, setLoadingEditLineId] = useState<number | null>(null)
  const [loadingViewLineId, setLoadingViewLineId] = useState<number | null>(null)

  const [editLineOpen, setEditLineOpen] = useState(false)
  const [editingLine, setEditingLine] = useState<PmsPlanLineDto | null>(null)
  const [editingLineVesselId, setEditingLineVesselId] = useState<number | null>(null)

  const [forceOverdueOpen, setForceOverdueOpen] = useState(false)
  const [forceOverdueTarget, setForceOverdueTarget] = useState<PmsPlanLineDto | null>(null)
  const [forceOverdueLoading, setForceOverdueLoading] = useState(false)
  const [forceOverdueForm, setForceOverdueForm] = useState<{
    jobDueReason: PmsJobDueReason
    remarks: string
  }>({
    jobDueReason: DEFAULT_FORCE_OVERDUE_REASON,
    remarks: '',
  })

  const [viewMode, setViewMode] = useState<CalendarView>('MONTH')
  const [anchorDate, setAnchorDate] = useState<Date>(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })

  const [rangeFrom, setRangeFrom] = useState<string>(() => {
    const today = new Date()
    return toDateKey(startOfMonth(today))
  })
  const [rangeTo, setRangeTo] = useState<string>(() => {
    const today = new Date()
    return toDateKey(endOfMonth(today))
  })

  const filtersDisabled = loading
  const [exporting, setExporting] = useState(false)

  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())
  const requestRef = useRef(0)
  const selectedVesselName = useMemo(() => {
    const selectedId = selectedVesselId ? Number(selectedVesselId) : null
    if (selectedId && vessels.length > 0) {
      const match = vessels.find((v) => Number(v.id) === selectedId)
      return match?.fleet_name || (match?.id != null ? `Vessel #${match.id}` : null)
    }
    if (isVesselOnlyUser && currentUser?.vessel) {
      return currentUser.vessel.fleet_name || `Vessel #${currentUser.vessel.id}`
    }
    if (!selectedVesselId) {
      return 'All Vessels'
    }
    return null
  }, [selectedVesselId, vessels, isVesselOnlyUser, currentUser])
  const customRange = useMemo(() => {
    if (!rangeFrom || !rangeTo) return null
    const start = new Date(rangeFrom)
    const end = new Date(rangeTo)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
    start.setHours(0, 0, 0, 0)
    end.setHours(23, 59, 59, 999)
    if (start > end) return null
    return {start, end}
  }, [rangeFrom, rangeTo])

  const viewRange = useMemo(() => {
    const base = new Date(anchorDate)
    if (viewMode === 'WEEK') {
      const start = new Date(base)
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      end.setHours(23, 59, 59, 999)
      return {start, end}
    }
    if (viewMode === 'MONTH') {
      const start = startOfMonth(base)
      const end = endOfMonth(base)
      return {start, end}
    }
    if (viewMode === 'YEAR') {
      const start = new Date(base.getFullYear(), 0, 1)
      const end = new Date(base.getFullYear(), 11, 31)
      end.setHours(23, 59, 59, 999)
      return {start, end}
    }
    if (customRange) {
      return customRange
    }
    const start = startOfMonth(base)
    const end = endOfMonth(base)
    return {start, end}
  }, [anchorDate, viewMode, customRange])

  const activeRange = viewMode === 'CUSTOM' && customRange ? customRange : viewRange

  const calendarMonths = useMemo(() => {
    if (viewMode === 'CUSTOM' && customRange) {
      const months: {year: number; month: number}[] = []
      const curr = new Date(customRange.start.getFullYear(), customRange.start.getMonth(), 1)
      const end = new Date(customRange.end.getFullYear(), customRange.end.getMonth(), 1)
      while (curr <= end && months.length < 24) {
        months.push({year: curr.getFullYear(), month: curr.getMonth()})
        curr.setMonth(curr.getMonth() + 1)
      }
      return months.length > 0
        ? months
        : [{year: anchorDate.getFullYear(), month: anchorDate.getMonth()}]
    }
    const base = new Date(anchorDate)
    const year = base.getFullYear()
    const month = base.getMonth()

    if (viewMode === 'MONTH') {
      return [{year, month}]
    }
    if (viewMode === 'YEAR') {
      return Array.from({length: 12}, (_, idx) => ({year, month: idx}))
    }
    return [{year, month}]
  }, [anchorDate, viewMode, customRange])

  const weekDays = useMemo(() => {
    const days: Date[] = []
    const start = new Date(anchorDate)
    start.setHours(0, 0, 0, 0)
    for (let i = 0; i < 7; i++) {
      const date = new Date(start)
      date.setDate(date.getDate() + i)
      days.push(date)
    }
    return days
  }, [anchorDate])

  const rangeLabel = useMemo(() => {
    if (viewMode === 'MONTH') {
      return formatMonthLabel(anchorDate.getFullYear(), anchorDate.getMonth())
    }
    if (viewMode === 'YEAR') {
      return `${anchorDate.getFullYear()}`
    }
    if (viewMode === 'WEEK') {
      return `${formatShortDate(viewRange.start)} - ${formatShortDate(viewRange.end)}`
    }
    if (customRange) {
      return `${formatShortDate(customRange.start)} - ${formatShortDate(customRange.end)}`
    }
    return 'Select Date Range'
  }, [viewMode, anchorDate, viewRange, customRange])

  useEffect(() => {
    loadVessels()
  }, [])

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest('[data-calendar-menu]')) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  useEffect(() => {
    loadCalendarData()
  }, [selectedVesselId, activeRange.start, activeRange.end, isVesselOnlyUser])

  const loadVessels = async () => {
    try {
      const vesselsData = await getVesselList()
      const vesselsForUser = getAccessibleVessels(vesselsData, currentUser, isSuperAdmin)

      setVessels(vesselsForUser)
      if (isVesselOnlyUser && currentUser?.vessel?.id) {
        setSelectedVesselId(String(currentUser.vessel.id))
      }
    } catch (error) {
      console.error('Error loading vessels:', error)
    }
  }

  const loadCalendarData = async () => {
    const vesselId = selectedVesselId
      ? Number(selectedVesselId)
      : isVesselOnlyUser
        ? currentUser?.vessel?.id
        : undefined
    if (isVesselOnlyUser && !vesselId) return

    const requestId = ++requestRef.current
    setLoading(true)
    try {
      const fromDate = toDateKey(activeRange.start)
      const toDate = toDateKey(activeRange.end)

      const pageSize = 200
      let page = 0
      let totalPages = 1
      const results: PmsJobCombinedDto[] = []

      while (page < totalPages) {
        const response = await searchPmsJobsCombined({
          vesselId: vesselId || undefined,
          fromDate,
          toDate,
          page,
          size: pageSize,
          sortBy: 'dueDate',
          sortDir: 'asc',
          dateFilterMode: 'CALENDAR',
        })
        if (requestId !== requestRef.current) return

        const content = response?.content ?? []
        results.push(...content)
        totalPages = response?.totalPages ?? 1
        page += 1
      }

      setCombinedItems(results)
    } catch (error: any) {
      if (requestId === requestRef.current) {
        console.error('Error loading calendar jobs:', error)
        toast.error('Failed to load calendar jobs', {position: 'top-center'})
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false)
      }
    }
  }

  const resolveJobCalendarDate = (job: PmsJobRecord, isClosed: boolean) => {
    if (isClosed) {
      return (
        job.actualCompletionDate ||
        (job as any).completionDate ||
        job.updatedAt ||
        job.dueDate ||
        job.plannedExecutionDate
      )
    }
    return job.dueDate || job.plannedExecutionDate || job.newDueDate || job.updatedAt
  }

  const isOverdueJob = useCallback((job: PmsJobRecord): boolean => {
    if (isMaskedJob(job)) return false
    if (job.state === 'OVERDUE') return true
    if (job.state === 'COMPLETED' || job.state === 'VERIFIED' || job.state === 'CLOSED') {
      return false
    }
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (job.dueDate) {
      const dueDate = new Date(job.dueDate)
      dueDate.setHours(0, 0, 0, 0)
      if (dueDate <= today) {
        return true
      }
    }

    if (job.dueCounter != null) {
      const counterVal = (job as any).runningHourCounterValue ?? (job as any).runningHours
      if (counterVal != null && counterVal >= job.dueCounter) {
        return true
      }
      if (job.state === 'IN_PROGRESS') {
        return true
      }
    }

    return false
  }, [])

  const isOpenState = (state: PmsJobState) => ['OVERDUE', 'IN_PROGRESS', 'DEFERRED'].includes(state)

  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const filteredCombined = useMemo(() => {
    return combinedItems.filter((item) => {
      if (allowedVesselIds != null) {
        const vId = Number(item.job?.vesselId ?? item.planLine?.vesselId)
        if (vId && !allowedVesselIds.has(vId)) return false
      }
      if (stateFilter !== 'ALL') {
        const isOverdue =
          item.source === 'periodicity'
            ? isPeriodicityOverdue(item.planLine)
            : isOverdueJob(item.job as PmsJobRecord)

        if (stateFilter === 'OVERDUE') {
          if (!isOverdue) return false
        } else if (
          stateFilter === 'COMPLETED' ||
          stateFilter === 'CLOSED' ||
          stateFilter === 'VERIFIED'
        ) {
          if (item.source === 'periodicity') return false
          if (item.job?.state !== stateFilter) return false
        } else {
          if (item.source === 'periodicity') {
            if (isOverdue) return false
          } else {
            if (getDisplayedJobState(item.job as PmsJobRecord) !== stateFilter) return false
          }
        }
      }
      const itemCriticality = item.planLine?.criticality ?? item.job?.criticality
      if (criticalityFilter !== 'ALL' && itemCriticality !== criticalityFilter) return false
      return true
    })
  }, [combinedItems, stateFilter, criticalityFilter, allowedVesselIds, isOverdueJob])

  const calendarItems = useMemo<CalendarItem[]>(() => {
    const items: CalendarItem[] = []

    filteredCombined.forEach((item) => {
      if (item.source === 'periodicity') {
        const planLine = item.planLine ?? undefined
        if (!planLine) return

        const isRunningHoursOnly = planLine.scheduleType === 'RUNNING_HOURS'
        if (isRunningHoursOnly) return

        const nextDueDate = planLine.nextDueDate
        if (!nextDueDate) return

        const date = new Date(nextDueDate)
        if (Number.isNaN(date.getTime())) return

        const overdue = isPeriodicityOverdue(planLine)
        items.push({
          id: `plan-${planLine.id}`,
          date,
          title: planLine.jobCode || planLine.taskDescription || `Plan Line #${planLine.id}`,
          subtitle: planLine.equipmentName || planLine.subComponentName || undefined,
          state: overdue ? 'OVERDUE' : 'PERIODICITY',
          criticality: planLine.criticality ?? null,
          source: 'periodicity',
          vesselId: item.job?.vesselId,
          vesselName: item.job?.vesselName,
          job: item.job as PmsJobRecord,
          planLine,
          isOverdue: overdue,
          isClosed: false,
          kind: overdue ? 'OVERDUE' : 'PLANNED',
        })
        return
      }

      const job = item.job as PmsJobRecord
      if (!job) return
      const isClosed =
        job.state === 'COMPLETED' || job.state === 'VERIFIED' || job.state === 'CLOSED'
      const overdue = isOverdueJob(job)
      const isUnplanned = !job.planLineId
      const planLine = item.planLine ?? undefined

      const pushJobItem = (kind: CalendarItemKind, dateValue?: string | null) => {
        if (!dateValue) return
        const date = new Date(dateValue)
        if (Number.isNaN(date.getTime())) return
        items.push({
          id: `job-${job.id ?? job.jobCode ?? Math.random()}-${kind}`,
          date,
          title: job.jobCode || job.title || `Job #${job.id}`,
          subtitle: job.equipmentName || job.subComponentName || undefined,
          state: job.state,
          criticality: job.criticality ?? null,
          source: 'job',
          vesselId: job.vesselId,
          vesselName: job.vesselName,
          job,
          planLine,
          isOverdue: overdue,
          isClosed,
          kind,
        })
      }

      if (isClosed) {
        pushJobItem(
          'CLOSED',
          job.actualCompletionDate || (job as any).completionDate || job.updatedAt
        )
      } else if (overdue) {
        pushJobItem('OVERDUE', job.dueDate || job.newDueDate || job.plannedExecutionDate)
      } else if (isUnplanned) {
        pushJobItem('UNPLANNED_CREATED', job.createdAt || job.updatedAt)
      } else {
        pushJobItem('JOB', resolveJobCalendarDate(job, isClosed))
      }
    })

    return items
  }, [filteredCombined, isOverdueJob])

  const filteredCalendarItems = useMemo(() => calendarItems, [calendarItems])
  const itemsByDate = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {}
    filteredCalendarItems.forEach((item) => {
      if (item.date < activeRange.start || item.date > activeRange.end) return
      const key = toDateKey(item.date)
      if (!map[key]) map[key] = []
      map[key].push(item)
    })

    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => {
        const overdueDiff = Number(b.isOverdue) - Number(a.isOverdue)
        if (overdueDiff !== 0) return overdueDiff
        const closedDiff = Number(b.isClosed) - Number(a.isClosed)
        if (closedDiff !== 0) return closedDiff
        const criticalityRank = (value?: string | null) => {
          switch (value) {
            case 'CRITICAL':
              return 4
            case 'HIGH':
              return 3
            case 'MEDIUM':
              return 2
            case 'LOW':
              return 1
            default:
              return 0
          }
        }
        const criticalityDiff = criticalityRank(b.criticality) - criticalityRank(a.criticality)
        if (criticalityDiff !== 0) return criticalityDiff
        return a.title.localeCompare(b.title)
      })
    })

    return map
  }, [filteredCalendarItems, activeRange.start, activeRange.end])

  const getItemsForDate = (date: Date) => {
    const key = toDateKey(date)
    return itemsByDate[key] ?? []
  }

  const summaryCounts = useMemo(() => {
    const items = calendarItems.filter(
      (item) => item.date >= activeRange.start && item.date <= activeRange.end
    )
    const overdue = items.filter((item) => item.kind === 'OVERDUE' || item.isOverdue).length
    const closed = items.filter((item) => item.kind === 'CLOSED' || item.isClosed).length
    const unplannedDefect = items.filter(
      (item) => item.kind === 'UNPLANNED_CREATED' && item.job?.jobDueReason === 'DEFECT'
    ).length
    const unplannedOther = items.filter(
      (item) => item.kind === 'UNPLANNED_CREATED' && item.job?.jobDueReason !== 'DEFECT'
    ).length
    const planned = items.filter(
      (item) => (item.kind === 'PLANNED' || item.kind === 'JOB') && !item.isOverdue && !item.isClosed
    ).length
    const scheduled = planned + unplannedDefect + unplannedOther
    const active = overdue + scheduled
    return {overdue, closed, unplannedDefect, unplannedOther, planned, scheduled, active}
  }, [calendarItems, activeRange.start, activeRange.end])

  const toggleExpandedDate = (key: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const navigateRange = (direction: 'prev' | 'next') => {
    const next = new Date(anchorDate)
    if (viewMode === 'WEEK') {
      next.setDate(next.getDate() + (direction === 'next' ? 7 : -7))
    } else if (viewMode === 'MONTH') {
      next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1))
    } else {
      next.setFullYear(next.getFullYear() + (direction === 'next' ? 1 : -1))
    }
    setAnchorDate(next)
  }

  const goToToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    setAnchorDate(today)
  }

  const openForceOverdueModal = (line: PmsPlanLineDto) => {
    if (!canCreateCalendar) {
      toast.error('You do not have permission to create unplanned jobs.', {position: 'top-center'})
      return
    }
    setForceOverdueTarget(line)
    setForceOverdueForm({jobDueReason: DEFAULT_FORCE_OVERDUE_REASON, remarks: ''})
    setForceOverdueOpen(true)
  }

  const closeForceOverdueModal = () => {
    setForceOverdueOpen(false)
    setForceOverdueTarget(null)
    setForceOverdueLoading(false)
  }

  const buildJobFromPlanLine = useCallback(
    (line: PmsPlanLineDto, item?: CalendarItem): PmsJobRecord => {
      const vesselId =
        item?.vesselId ??
        item?.job?.vesselId ??
        (selectedVesselId ? Number(selectedVesselId) : null) ??
        currentUser?.vessel?.id ??
        0
      const vesselName =
        item?.vesselName ||
        item?.job?.vesselName ||
        (selectedVesselId ? selectedVesselName : null) ||
        currentUser?.vessel?.fleet_name ||
        (currentUser?.vessel?.id != null ? `Vessel #${currentUser.vessel.id}` : null)
      const isOverdue = item?.isOverdue || item?.kind === 'OVERDUE'
      const title =
        line.taskDescription ||
        line.jobCode ||
        (line.id != null ? `Plan Line #${line.id}` : 'Planned Job')

      return {
        id: undefined,
        planLineId: line.id ?? null,
        vesselId,
        vesselName: vesselName ?? null,
        equipmentId: line.equipmentId ?? null,
        equipmentName: line.equipmentName ?? null,
        componentId: line.componentId ?? null,
        componentName: line.componentName ?? null,
        subComponentId: line.subComponentId ?? null,
        subComponentName: line.subComponentName ?? null,
        jobCode: line.jobCode ?? null,
        jobType: line.jobType ?? null,
        jobDescription: line.taskDescription ?? null,
        jobPeriodicity: line.jobPeriodicity ?? null,
        periodicityId: line.periodicityId ?? null,
        jobPeriodicity2: line.jobPeriodicity2 ?? null,
        periodicityId2: line.periodicityId2 ?? null,
        crewUndertaking: line.crewUndertaking ?? null,
        jobInstructions: line.jobInstructions ?? null,
        componentInstanceId: line.componentInstanceId ?? null,
        subComponentInstanceId: line.subComponentInstanceId ?? null,
        title,
        scheduleType: line.scheduleType ?? null,
        criticality: line.criticality ?? null,
        lastDoneDate: line.lastDoneDate ?? null,
        lastDoneCounter: line.lastDoneCounter ?? null,
        dueDate: line.nextDueDate ?? null,
        dueCounter: line.nextDueCounter ?? null,
        state: isOverdue ? 'OVERDUE' : 'IN_PROGRESS',
        createdAt: line.createdAt ?? null,
        updatedAt: line.updatedAt ?? null,
      }
    },
    [currentUser?.vessel?.fleet_name, currentUser?.vessel?.id, selectedVesselId, selectedVesselName]
  )

  const handleConfirmForceOverdue = async () => {
    if (!canCreateCalendar) {
      toast.error('You do not have permission to create unplanned jobs.', {position: 'top-center'})
      return
    }
    if (!forceOverdueTarget?.id || forceOverdueLoading) return
    setForceOverdueLoading(true)
    try {
      await forceOverduePlanLineJob(forceOverdueTarget.id, {
        jobDueReason: forceOverdueForm.jobDueReason || DEFAULT_FORCE_OVERDUE_REASON,
        remarks: forceOverdueForm.remarks?.trim() || undefined,
      })
      toast.success('Unplanned job created', {position: 'top-center'})
      closeForceOverdueModal()
      loadCalendarData()
    } catch (error: any) {
      console.error('Error creating unplanned job:', error)
      toast.error('Failed to create unplanned job', {position: 'top-center'})
      setForceOverdueLoading(false)
    }
  }

  const handleViewJobDetails = async (item: CalendarItem) => {
    if (item.job) {
      setViewing(item.job)
      setViewOpen(true)
      setOpenMenuId(null)
      return
    }

    const lineId = getPlanLineIdFromItem(item)
    if (!lineId) return

    setLoadingViewLineId(lineId)
    try {
      const workOrders = await listWorkOrdersByPlanLine(lineId)
      const getSortTime = (wo: PmsJobRecord) => {
        const raw = wo.actualCompletionDate || wo.updatedAt || wo.createdAt || ''
        const dt = raw ? new Date(raw).getTime() : 0
        return Number.isNaN(dt) ? 0 : dt
      }
      const sorted = [...workOrders].sort((a, b) => getSortTime(b) - getSortTime(a))
      const latest = sorted[0]
      if (!latest) {
        let planLine = item.planLine
        if (!planLine) {
          planLine = await getPmsPlanLine(lineId)
        }
        if (planLine) {
          setViewing(buildJobFromPlanLine(planLine, item))
          setViewOpen(true)
          return
        }
        toast.info('No job record found for this periodicity.', {position: 'top-center'})
        return
      }
      setViewing(latest)
      setViewOpen(true)
    } catch (error: any) {
      console.error('Error loading job details:', error)
      toast.error('Failed to load job details', {position: 'top-center'})
    } finally {
      setLoadingViewLineId(null)
      setOpenMenuId(null)
    }
  }

  const handleOpenCompleteJob = (item: CalendarItem) => {
    if (!canDeleteCalendar) {
      toast.error('You do not have permission to close jobs.', {position: 'top-center'})
      return
    }
    if (!item.job || !item.job.id) {
      toast.info('No job record available to close.', {position: 'top-center'})
      return
    }
    setCompleting({
      ...item.job,
      __planLine: item.planLine ?? undefined,
    })
    setCompleteOpen(true)
    setOpenMenuId(null)
  }

  const handleEditPeriodicity = async (item: CalendarItem) => {
    if (!canUpdateCalendar) {
      toast.error('You do not have permission to edit periodicities.', {position: 'top-center'})
      return
    }
    const targetVesselId = item.vesselId || item.job?.vesselId || calendarEditVesselId
    setEditingLineVesselId(targetVesselId || null)

    if (item.planLine) {
      setEditingLine(item.planLine)
      setEditLineOpen(true)
      setOpenMenuId(null)
      return
    }
    const lineId = getPlanLineIdFromItem(item)
    if (!lineId) return
    setLoadingEditLineId(lineId)
    try {
      const fetched = await getPmsPlanLine(lineId)
      setEditingLine(fetched)
      setEditLineOpen(true)
    } catch (error: any) {
      console.error('Error loading plan line:', error)
      toast.error('Failed to load periodicity (plan line)', {
        position: 'top-center',
      })
    } finally {
      setLoadingEditLineId(null)
      setOpenMenuId(null)
    }
  }

  const formatExportDate = (value: Date) => value.toISOString().slice(0, 10)

  const handleExport = () => {
    if (!canExportPms) {
      toast.error('You do not have permission to export PMS data.', {position: 'top-center'})
      return
    }
    if (exporting) return
    const rangeItems = calendarItems.filter(
      (item) => item.date >= activeRange.start && item.date <= activeRange.end
    )
    if (rangeItems.length === 0) {
      toast.info('No data to export for the selected filters.', {position: 'top-center'})
      return
    }

    setExporting(true)
    try {
      const getVessel = (item: CalendarItem) =>
        item.vesselName || item.job?.vesselName || selectedVesselName || ''
      const getDescription = (item: CalendarItem) =>
        item.job?.jobDescription || item.planLine?.taskDescription || item.job?.title || item.title
      const getSubcomponent = (item: CalendarItem) =>
        item.job?.subComponentName || item.planLine?.subComponentName || item.subtitle || ''
      const getEquipment = (item: CalendarItem) =>
        item.job?.equipmentName || item.planLine?.equipmentName || ''
      const getJobCode = (item: CalendarItem) => item.job?.jobCode || item.planLine?.jobCode || ''

      const overdueRows = rangeItems
        .filter((item) => item.kind === 'OVERDUE' || item.kind === 'PLANNED' || item.kind === 'JOB')
        .map((item) => ({
          Date: formatExportDate(item.date),
          Vessel: getVessel(item),
          Status: item.kind === 'OVERDUE' || item.isOverdue ? 'OVERDUE' : 'SCHEDULED',
          Description: getDescription(item),
          JobCode: getJobCode(item),
          Equipment: getEquipment(item),
          Subcomponent: getSubcomponent(item),
          Criticality: item.job?.criticality || item.planLine?.criticality || '',
        }))

      const closedRows = rangeItems
        .filter((item) => item.kind === 'CLOSED')
        .map((item) => ({
          Date: formatExportDate(item.date),
          Vessel: getVessel(item),
          Description: getDescription(item),
          JobCode: getJobCode(item),
          Equipment: getEquipment(item),
          Subcomponent: getSubcomponent(item),
          State: item.job?.state || '',
        }))

      const unplannedRows = rangeItems
        .filter((item) => item.kind === 'UNPLANNED_CREATED')
        .map((item) => ({
          Date: formatExportDate(item.date),
          Vessel: getVessel(item),
          Description: getDescription(item),
          JobCode: getJobCode(item),
          Equipment: getEquipment(item),
          Subcomponent: getSubcomponent(item),
          Defect: item.job?.jobDueReason === 'DEFECT' ? 'Yes' : 'No',
          DefectNumber: item.job?.defectNumber || '',
        }))

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(overdueRows),
        'Overdue Instances'
      )
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(closedRows),
        'Closed Instances'
      )
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(unplannedRows),
        'Unplanned Jobs'
      )

      const fileLabel = `${viewMode.toLowerCase()}-${formatExportDate(activeRange.start)}-to-${formatExportDate(activeRange.end)}`
      XLSX.writeFile(workbook, `calendar-planning-${fileLabel}.xlsx`)
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export calendar data', {position: 'top-center'})
    } finally {
      setExporting(false)
    }
  }

  const renderHoverCard = (item: CalendarItem) => {
    if (hoveredItemId !== item.id) return null
    const description =
      item.job?.jobDescription || item.planLine?.taskDescription || item.job?.title || item.title
    const subcomponentLabel =
      item.job?.subComponentName || item.planLine?.subComponentName || item.subtitle
    return (
      <div
        className='position-absolute bg-white border rounded-3 shadow-sm p-3'
        style={{zIndex: 20, minWidth: '220px', top: '100%', left: 0}}
      >
        <div className='fw-bold text-gray-900 mb-1'>{description || item.title}</div>
        {subcomponentLabel && (
          <div className='mb-1'>
            <span className='badge badge-light-info fs-9'>{subcomponentLabel}</span>
          </div>
        )}
        {item.job?.jobDueReason && (
          <div className='fs-8 text-muted'>Reason: {item.job.jobDueReason}</div>
        )}
        {item.job?.state && (
          <div className='fs-8 text-muted'>State: {getDisplayedJobState(item.job)}</div>
        )}
        {(item.vesselName || item.job?.vesselName) && (
          <div className='fs-8 text-muted'>Vessel: {item.vesselName || item.job?.vesselName}</div>
        )}
        <div className='fs-8 text-muted mt-1'>Date: {item.date.toLocaleDateString()}</div>
      </div>
    )
  }

  const renderItem = (item: CalendarItem) => {
    const borderColor = item.isOverdue
      ? '#F1416C'
      : item.kind === 'CLOSED'
        ? '#A1A5B7'
        : item.criticality === 'CRITICAL' || item.criticality === 'HIGH'
          ? '#F1416C'
          : item.criticality === 'MEDIUM'
            ? '#FFC700'
            : '#50CD89'

    const badgeLabel =
      item.kind === 'PLANNED'
        ? 'Planned'
        : item.kind === 'OVERDUE'
          ? 'Overdue'
          : item.kind === 'CLOSED'
            ? 'Closed'
          : item.kind === 'UNPLANNED_CREATED'
              ? 'Unplanned'
              : item.job
                ? getDisplayedJobState(item.job)
                : item.state

    const isMenuOpen = openMenuId === item.id
    const canCreateUnplanned = item.source === 'periodicity' && !!item.planLine && !item.isOverdue
    const planLineId = getPlanLineIdFromItem(item)
    const canEditPeriodicity = !!planLineId
    const canViewJob = canReadCalendar && (!!item.job || !!planLineId)
    const isEditingThis =
      loadingEditLineId != null && planLineId != null && loadingEditLineId === planLineId
    const isViewingThis =
      loadingViewLineId != null && planLineId != null && loadingViewLineId === planLineId
    const titleText =
      item.job?.jobDescription || item.job?.title || item.planLine?.taskDescription || item.title
    const subcomponentLabel =
      item.job?.subComponentName || item.planLine?.subComponentName || item.subtitle

    return (
      <div
        key={item.id}
        className='p-2 rounded-2 border border-gray-200 bg-white position-relative'
        style={{borderLeft: `3px solid ${borderColor}`}}
        onMouseEnter={() => setHoveredItemId(item.id)}
        onMouseLeave={() => setHoveredItemId(null)}
        data-calendar-menu
      >
        <div className='d-flex justify-content-between align-items-start gap-2'>
          <div
            className='fw-semibold fs-8 text-gray-900'
            style={{
              lineHeight: 1.2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
            title={titleText}
          >
            {titleText}
          </div>
          <div className='d-flex align-items-center gap-1'>
            <span
              className={`badge badge-light-${
                item.kind === 'OVERDUE'
                  ? 'danger'
                  : item.kind === 'CLOSED'
                    ? 'success'
                    : item.kind === 'UNPLANNED_CREATED'
                      ? 'warning'
                      : 'primary'
              } fs-9`}
            >
              {badgeLabel}
            </span>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light'
              onClick={(e) => {
                e.stopPropagation()
                setOpenMenuId((prev) => (prev === item.id ? null : item.id))
              }}
            >
              <KTSVG path='/media/icons/duotune/general/gen024.svg' className='svg-icon-4' />
            </button>
          </div>
        </div>
        {subcomponentLabel && (
          <div className='mt-1'>
            <span
              className='badge badge-light-info fs-9'
              style={{
                maxWidth: '100%',
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={subcomponentLabel}
            >
              {subcomponentLabel}
            </span>
          </div>
        )}
        {!selectedVesselId && (item.vesselName || item.job?.vesselName) && (
          <div className='mt-1'>
            <span
              className='badge badge-light-secondary fs-9 text-dark'
              style={{
                maxWidth: '100%',
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={item.vesselName || item.job?.vesselName || ''}
            >
              {item.vesselName || item.job?.vesselName}
            </span>
          </div>
        )}
        {renderHoverCard(item)}

        {isMenuOpen && (
          <div
            className='position-absolute bg-white border rounded shadow-sm p-2'
            style={{zIndex: 25, right: 0, top: '100%', minWidth: '180px'}}
          >
            {canViewJob && (
              <button
                type='button'
                className='btn btn-sm btn-light w-100 text-start mb-1'
                disabled={isViewingThis}
                onClick={() => handleViewJobDetails(item)}
              >
                {isViewingThis ? 'Loading Job Details...' : 'View Job Details'}
              </button>
            )}
            {!!item.job &&
              isOpenState(item.job.state) &&
              isOverdueJob(item.job) &&
              canDeleteCalendar && (
                <button
                  type='button'
                  className='btn btn-sm btn-success w-100 text-start mb-1'
                  onClick={() => handleOpenCompleteJob(item)}
                >
                  Close Job
                </button>
              )}
            {canEditPeriodicity && canUpdateCalendar && (
              <button
                type='button'
                className='btn btn-sm btn-light w-100 text-start mb-1'
                disabled={isEditingThis}
                onClick={() => handleEditPeriodicity(item)}
              >
                {isEditingThis ? 'Loading Periodicity...' : 'Edit Periodicity'}
              </button>
            )}
            {canCreateUnplanned && canCreateCalendar && (
              <button
                type='button'
                className='btn btn-sm btn-light w-100 text-start'
                onClick={() => {
                  openForceOverdueModal(item.planLine!)
                  setOpenMenuId(null)
                }}
              >
                Create Unplanned Job
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  const buildMonthGrid = (year: number, month: number) => {
    const start = startOfWeek(new Date(year, month, 1))
    const end = endOfWeek(new Date(year, month + 1, 0))

    const days: Date[] = []
    const cursor = new Date(start)
    while (cursor <= end) {
      days.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }

    return (
      <div className='border rounded-4 bg-white shadow-sm overflow-hidden'>
        <div className='d-flex align-items-center justify-content-between px-4 py-3 border-bottom'>
          <div className='fw-bold text-gray-900'>{formatMonthLabel(year, month)}</div>
          <div className='text-muted fs-8'>
            {formatShortDate(new Date(year, month, 1))} -{' '}
            {formatShortDate(new Date(year, month + 1, 0))}
          </div>
        </div>
        <div
          className='d-grid border-top border-start border-gray-300'
          style={{gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderColor: '#E1E3EA'}}
        >
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className='text-center text-gray-800 fs-8 fw-bold border-bottom border-end border-gray-300 py-2 bg-light-primary'
              style={{borderColor: '#E1E3EA'}}
            >
              {label}
            </div>
          ))}
          {days.map((day, index) => {
            const key = toDateKey(day)
            const isInMonth = day.getMonth() === month
            const isToday = day.toDateString() === new Date().toDateString()
            const isInCustomRange =
              !!customRange && day >= customRange.start && day <= customRange.end
            const items = getItemsForDate(day)
            const isExpanded = expandedDates.has(key)
            const maxVisible = 3
            const visibleItems = isExpanded ? items : items.slice(0, maxVisible)
            const remaining = items.length - visibleItems.length
            const isExpandable = items.length > maxVisible

            return (
              <div
                key={key}
                className={`p-2 border-bottom border-end border-gray-300 ${
                  isInMonth ? 'bg-white' : 'bg-light text-muted'
                }`}
                style={{
                  minHeight: '120px',
                  borderColor: '#E1E3EA',
                  backgroundColor: isInCustomRange ? '#F1F9FF' : undefined,
                }}
              >
                <div className='d-flex align-items-center justify-content-between mb-1'>
                  <div className={`fw-semibold fs-8 ${isToday ? 'text-primary' : ''}`}>
                    {day.getDate()}
                  </div>
                  {isToday && <span className='badge badge-light-primary fs-9'>Today</span>}
                </div>
                <div className='d-flex flex-column gap-1'>
                  {visibleItems.map(renderItem)}
                  {isExpandable && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light w-100 fs-9'
                      onClick={() => toggleExpandedDate(key)}
                    >
                      {isExpanded ? 'Show less' : `+${remaining} more`}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderWeekView = () => {
    return (
      <div
        className='d-grid'
        style={{gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px'}}
      >
        {weekDays.map((day) => {
          const key = toDateKey(day)
          const isToday = day.toDateString() === new Date().toDateString()
          const isInCustomRange =
            !!customRange && day >= customRange.start && day <= customRange.end
          const items = getItemsForDate(day)
          return (
            <div
              key={key}
              className={`border rounded-4 p-3 ${isToday ? 'border-primary' : 'border-gray-200'}`}
              style={{minHeight: '240px', backgroundColor: isInCustomRange ? '#F1F9FF' : '#ffffff'}}
            >
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <div>
                  <div className='fw-bold'>
                    {day.toLocaleDateString('en-US', {weekday: 'long'})}
                  </div>
                  <div className='text-muted fs-8'>{formatShortDate(day)}</div>
                </div>
                {isToday && <span className='badge badge-light-primary fs-9'>Today</span>}
              </div>
              <div className='d-flex flex-column gap-2'>
                {items.length === 0 ? (
                  <div className='text-muted fs-8'>No jobs scheduled</div>
                ) : (
                  items.map(renderItem)
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const monthInputValue = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}`
  const weekInputValue = toDateKey(anchorDate)

  const handleSelectViewMode = (mode: CalendarView) => {
    if (mode === 'CUSTOM') {
      setRangeFrom(toDateKey(viewRange.start))
      setRangeTo(toDateKey(viewRange.end))
    }
    setViewMode(mode)
  }

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({length: 7}, (_, idx) => currentYear - 3 + idx)
  }, [])

  const parsedCalendarVessel = selectedVesselId ? Number(selectedVesselId) : NaN
  const calendarEditVesselId = Number.isFinite(parsedCalendarVessel)
    ? parsedCalendarVessel
    : currentUser?.vessel?.id
      ? Number(currentUser.vessel.id)
      : 0

  if (!canReadCalendar) {
    return (
      <TechnicalAccessDenied
        title='Calendar planning'
        message='You do not have permission to view PMS calendar planning (READ_PMS_CALENDAR).'
      />
    )
  }

  return (
    <div className='card border-0 shadow-sm'>
      <div className='card-header border-0 pt-6 pb-4'>
        <div className='d-flex flex-column'>
          <h3 className='fw-bold m-0'>Calendar Planning</h3>
          <div className='text-muted fs-7 mt-1'>{rangeLabel}</div>
          <div className='d-flex flex-wrap align-items-center gap-1 mt-3 text-muted fs-7'>
            <span>Jobs:</span>
            <strong className='text-dark'>{summaryCounts.active} Active</strong>
            <span className='mx-1'>|</span>

            <span>Overdue:</span>
            <strong className={summaryCounts.overdue > 0 ? 'text-danger' : 'text-dark'}>
              {summaryCounts.overdue}
            </strong>
            <span className='mx-1'>|</span>

            <span>Scheduled:</span>
            <strong className='text-dark'>{summaryCounts.scheduled}</strong>
            <span className='mx-1'>|</span>

            <span>Completed:</span>
            <strong className='text-dark'>{summaryCounts.closed} Total</strong>
          </div>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            {selectedVesselName && (
              <span className='badge badge-light-primary fw-semibold'>
                {selectedVesselId ? `Selected Vessel: ${selectedVesselName}` : 'All Vessels'}
              </span>
            )}

            <SearchableSelect
              className='form-select form-select-sm form-select-solid w-150px'
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as PmsJobState | 'ALL')}
              disabled={filtersDisabled}
            >
              <option value='ALL'>All States</option>
              <option value='OVERDUE'>Overdue</option>
              <option value='COMPLETED'>Completed</option>
              <option value='VERIFIED'>Verified</option>
              <option value='CLOSED'>Closed</option>
            </SearchableSelect>

            <SearchableSelect
              className='form-select form-select-sm form-select-solid w-150px'
              value={criticalityFilter}
              onChange={(e) => setCriticalityFilter(e.target.value as PmsCriticality | 'ALL')}
              disabled={filtersDisabled}
            >
              <option value='ALL'>All Criticality</option>
              <option value='CRITICAL'>Critical</option>
              <option value='HIGH'>High</option>
              <option value='MEDIUM'>Medium</option>
              <option value='LOW'>Low</option>
            </SearchableSelect>

            <button
              className='btn btn-sm btn-light-success'
              onClick={handleExport}
              disabled={filtersDisabled || exporting || !canExportPms}
              title='Export calendar data'
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <div className='card-body pt-4'>
        {/* Unified Calendar Navigation & Filter Toolbar */}
        <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-5 p-3 bg-light rounded-3 border border-gray-200'>
          {/* View Mode Segmented Controls */}
          <div className='d-flex align-items-center bg-white p-1 rounded-2 shadow-xs border border-gray-200 gap-1'>
            {[
              {key: 'MONTH', label: 'Monthly'},
              {key: 'WEEK', label: 'Weekly'},
              {key: 'YEAR', label: 'Annually'},
              {key: 'CUSTOM', label: 'Custom Range'},
            ].map((tab) => {
              const isActive = viewMode === tab.key
              return (
                <button
                  key={tab.key}
                  type='button'
                  className={`btn btn-sm px-4 py-2 rounded-2 fw-bold ${
                    isActive ? 'btn-primary shadow-xs' : 'btn-light text-gray-600'
                  }`}
                  style={{
                    backgroundColor: isActive ? '#009EF7' : 'transparent',
                    color: isActive ? '#FFFFFF' : '#5E6278',
                    border: 'none',
                  }}
                  onClick={() => handleSelectViewMode(tab.key as CalendarView)}
                  disabled={filtersDisabled}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Unified Navigation & Active Period Controls */}
          <div className='d-flex flex-wrap align-items-center gap-2'>
            {viewMode !== 'CUSTOM' && (
              <div className='btn-group btn-group-sm me-1'>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-white border shadow-xs'
                  onClick={() => navigateRange('prev')}
                  title='Previous'
                  disabled={filtersDisabled}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3' />
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-white border shadow-xs px-3 fw-bold text-primary'
                  onClick={goToToday}
                  title='Go to Current Period'
                  disabled={filtersDisabled}
                >
                  Today
                </button>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-white border shadow-xs'
                  onClick={() => navigateRange('next')}
                  title='Next'
                  disabled={filtersDisabled}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3' />
                </button>
              </div>
            )}

            {/* Monthly Picker */}
            {viewMode === 'MONTH' && (
              <div className='d-flex align-items-center gap-2 bg-white rounded-2 px-3 py-1 border shadow-xs'>
                <KTSVG path='/media/icons/duotune/general/gen014.svg' className='svg-icon-3 text-muted' />
                <span className='fs-8 fw-semibold text-muted'>Month:</span>
                <input
                  type='month'
                  className='form-control form-control-sm border-0 bg-transparent fw-bold text-dark p-0'
                  style={{width: '135px'}}
                  value={monthInputValue}
                  onChange={(e) => {
                    if (!e.target.value) return
                    const [y, m] = e.target.value.split('-').map(Number)
                    if (!y || !m) return
                    const next = new Date(anchorDate)
                    next.setFullYear(y)
                    next.setMonth(m - 1)
                    next.setDate(1)
                    setAnchorDate(next)
                  }}
                  disabled={filtersDisabled}
                />
              </div>
            )}

            {/* Annually Picker */}
            {viewMode === 'YEAR' && (
              <div className='d-flex align-items-center gap-2 bg-white rounded-2 px-3 py-1 border shadow-xs'>
                <span className='fs-8 fw-semibold text-muted'>Year:</span>
                <SearchableSelect
                  className='form-select form-select-sm border-0 bg-transparent fw-bold text-dark w-100px p-0'
                  value={anchorDate.getFullYear()}
                  onChange={(e) => {
                    const year = Number(e.target.value)
                    if (!year) return
                    const next = new Date(anchorDate)
                    next.setFullYear(year)
                    next.setMonth(0)
                    next.setDate(1)
                    setAnchorDate(next)
                  }}
                  disabled={filtersDisabled}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {/* Weekly Picker */}
            {viewMode === 'WEEK' && (
              <div className='d-flex align-items-center gap-2 bg-white rounded-2 px-3 py-1 border shadow-xs'>
                <KTSVG path='/media/icons/duotune/general/gen014.svg' className='svg-icon-3 text-muted' />
                <span className='fs-8 fw-semibold text-muted'>Week of:</span>
                <input
                  type='date'
                  className='form-control form-control-sm border-0 bg-transparent fw-bold text-dark p-0'
                  style={{width: '140px'}}
                  value={weekInputValue}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value) {
                      const [y, m, d] = value.split('-').map(Number)
                      if (y && m && d) {
                        const date = new Date(y, m - 1, d)
                        date.setHours(0, 0, 0, 0)
                        setAnchorDate(date)
                      }
                    }
                  }}
                  disabled={filtersDisabled}
                />
              </div>
            )}

            {/* Custom Range Picker */}
            {viewMode === 'CUSTOM' && (
              <div className='d-flex align-items-center gap-2 bg-white rounded-2 px-3 py-1 border shadow-xs'>
                <KTSVG path='/media/icons/duotune/general/gen014.svg' className='svg-icon-3 text-muted' />
                <input
                  type='date'
                  className='form-control form-control-sm border-0 bg-transparent p-0'
                  style={{width: '135px'}}
                  value={rangeFrom || toDateKey(viewRange.start)}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  disabled={filtersDisabled}
                />
                <span className='text-muted fs-8'>to</span>
                <input
                  type='date'
                  className='form-control form-control-sm border-0 bg-transparent p-0'
                  style={{width: '135px'}}
                  value={rangeTo || toDateKey(viewRange.end)}
                  onChange={(e) => setRangeTo(e.target.value)}
                  disabled={filtersDisabled}
                />
                <button
                  type='button'
                  className='btn btn-xs btn-light-secondary ms-1 px-2 py-1 fs-9'
                  onClick={() => {
                    setRangeFrom(toDateKey(viewRange.start))
                    setRangeTo(toDateKey(viewRange.end))
                  }}
                  title='Reset to current period'
                  disabled={filtersDisabled}
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className='text-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : viewMode === 'WEEK' ? (
          renderWeekView()
        ) : (
          <div className='d-grid gap-4' style={{gridTemplateColumns: '1fr'}}>
            {calendarMonths.map(({year, month}) => buildMonthGrid(year, month))}
          </div>
        )}
      </div>

      {viewOpen && viewing && (
        <PmsJobDetailModal
          visible={viewOpen}
          onClose={() => {
            setViewOpen(false)
            setViewing(null)
          }}
          record={viewing}
          onRefresh={loadCalendarData}
        />
      )}
      {canDeleteCalendar && completeOpen && completing && (
        <CompleteJobModal
          visible={completeOpen}
          onClose={() => {
            setCompleteOpen(false)
            setCompleting(null)
          }}
          job={completing}
          onSuccess={() => {
            setCompleteOpen(false)
            setCompleting(null)
            loadCalendarData()
          }}
        />
      )}

      {canUpdateCalendar &&
        editLineOpen &&
        editingLine &&
        (editingLineVesselId || calendarEditVesselId) > 0 && (
          <EditPlanLineModal
            visible={editLineOpen}
            planLine={editingLine}
            vesselId={(editingLineVesselId || calendarEditVesselId)!}
            onClose={() => {
              setEditLineOpen(false)
              setEditingLine(null)
              setEditingLineVesselId(null)
            }}
            onSuccess={() => {
              setEditLineOpen(false)
              setEditingLine(null)
              setEditingLineVesselId(null)
              loadCalendarData()
            }}
          />
        )}

      {canCreateCalendar && forceOverdueOpen && forceOverdueTarget && (
        <div
          style={MODAL_OVERLAY_STYLE}
          tabIndex={-1}
          onClick={(e) => e.target === e.currentTarget && closeForceOverdueModal()}
        >
          <div
            className='bg-white text-dark rounded shadow-lg d-flex flex-column'
            style={{width: '520px', maxWidth: '92vw'}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3'>
              <h5 className='modal-title text-dark m-0'>Create Unplanned Job</h5>
              <button type='button' className='btn-close' onClick={closeForceOverdueModal}></button>
            </div>
            <div className='px-4 py-3'>
              <p className='text-muted mb-3'>
                This will mark the selected periodicity as overdue immediately and create an
                unplanned job.
              </p>
              <div className='border rounded p-3 bg-light'>
                <div className='mb-1'>
                  <strong>Equipment:</strong> {forceOverdueTarget.equipmentName || '-'}
                </div>
                <div className='mb-1'>
                  <strong>Sub-Component:</strong> {forceOverdueTarget.subComponentName || '-'}
                </div>
                <div className='mb-1'>
                  <strong>Job Code:</strong> {forceOverdueTarget.jobCode || '-'}
                </div>
                <div className='mb-1'>
                  <strong>Job Description:</strong> {forceOverdueTarget.taskDescription || '-'}
                </div>
                <div className='mb-1'>
                  <strong>Schedule Type:</strong>{' '}
                  {scheduleTypeLabel(forceOverdueTarget.scheduleType)}
                </div>
              </div>
              <div className='mt-3'>
                <label className='form-label fw-semibold fs-7 text-dark'>Job Due Reason</label>
                <SearchableSelect
                  className='form-select text-dark'
                  value={forceOverdueForm.jobDueReason}
                  onChange={(e) =>
                    setForceOverdueForm((prev) => ({
                      ...prev,
                      jobDueReason: e.target.value as PmsJobDueReason,
                    }))
                  }
                >
                  {FORCE_OVERDUE_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className='mt-3'>
                <label className='form-label fw-semibold fs-7 text-dark'>Remarks</label>
                <textarea
                  className='form-control text-dark'
                  rows={3}
                  value={forceOverdueForm.remarks}
                  onChange={(e) =>
                    setForceOverdueForm((prev) => ({...prev, remarks: e.target.value}))
                  }
                  placeholder='Notes for why this job is being forced overdue'
                />
              </div>
            </div>
            <div className='border-top d-flex justify-content-end gap-2 px-4 py-3'>
              <button
                type='button'
                className='btn btn-light btn-sm'
                onClick={closeForceOverdueModal}
                disabled={forceOverdueLoading}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-danger btn-sm'
                onClick={handleConfirmForceOverdue}
                disabled={forceOverdueLoading}
              >
                {forceOverdueLoading ? 'Creating...' : 'Create Unplanned Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />
    </div>
  )
}

export default CalendarPlanningBoard
